import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip, IconButton, TextField, Typography } from "@mui/material";
import { Add, Close } from "@mui/icons-material";
import { useAppTheme } from "@/hooks/useAppTheme";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { FormSection } from "@/components/common/inputs/FormSection";
import { EditableCodeChipSection } from "@/resources/policies/components/EditableCodeChipSection";
import { ClaimIdPicker } from "@/resources/policies/components/ClaimIdPicker";
import {
  ALL_QUALIFYING_CONDITIONS,
  type EndorsementType,
  type QualifyingCondition,
  type TieredDeductibleTier,
  type AdminChangeEntry,
  type AmendmentEntry,
  type DomesticReimbursementEntry,
  type EndorsementCodes,
} from "@/types";
import { getJunctionSchema, type JunctionSchema } from "./endorsementFieldSchemas";

// ─────────────────────────────────────────────────────────────
// Public props
// ─────────────────────────────────────────────────────────────

interface JunctionFieldsCreateProps {
  endorsementType: EndorsementType;
  policyId: string;
  /** Ref callback — parent stores a function that returns current junction data */
  getJunctionDataRef: React.MutableRefObject<(() => Record<string, unknown>) | null>;
  /** Fires whenever junction state changes — used by PolicyCreateInputs to sync to form state */
  onStateChange?: (data: Record<string, unknown>) => void;
  /** When true, skip the FormSection wrapper (for embedding inside other sections) */
  compact?: boolean;
}

// ─────────────────────────────────────────────────────────────
// State shape
// ─────────────────────────────────────────────────────────────

type JunctionState = Record<string, unknown>;

/** Build initial empty state for every junction key in the schema */
const buildInitialState = (schemas: JunctionSchema[]): JunctionState => {
  const initial: JunctionState = {};
  for (const s of schemas) {
    switch (s.junctionType) {
      case "codes":
        initial[s.key] = { icd: [], cpt: [] } as EndorsementCodes;
        break;
      default:
        initial[s.key] = [];
        break;
    }
  }
  return initial;
};

/** Return only non-empty junction data for the payload */
const collectPayload = (state: JunctionState): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(state)) {
    if (Array.isArray(value) && value.length > 0) {
      result[key] = value;
    } else if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      const codes = value as EndorsementCodes;
      if ((codes.icd?.length ?? 0) > 0 || (codes.cpt?.length ?? 0) > 0) {
        result[key] = value;
      }
    }
  }
  return result;
};

// ─────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────

export const JunctionFieldsCreate = ({
  endorsementType,
  policyId,
  getJunctionDataRef,
  onStateChange,
  compact = false,
}: JunctionFieldsCreateProps) => {
  const schemas = useMemo(() => getJunctionSchema(endorsementType), [endorsementType]);
  const [state, setState] = useState<JunctionState>(() => buildInitialState(schemas));

  // Reset state when endorsement type changes
  useEffect(() => {
    setState(buildInitialState(schemas));
  }, [schemas]);

  // Expose getter to parent
  useEffect(() => {
    getJunctionDataRef.current = () => collectPayload(state);
  }, [state, getJunctionDataRef]);

  // Notify parent of state changes (used by PolicyCreateInputs for form state sync)
  useEffect(() => {
    onStateChange?.(collectPayload(state));
  }, [state, onStateChange]);

  /** Type-safe state updater for a specific junction key */
  const updateField = useCallback((key: string, value: unknown) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  if (schemas.length === 0) return null;

  const content = schemas.map((schema) => (
    <JunctionField
      key={schema.key}
      schema={schema}
      value={state[schema.key]}
      onChange={(val) => updateField(schema.key, val)}
      policyId={policyId}
    />
  ));

  if (compact) return <>{content}</>;

  return (
    <FormSection title="Details" subtitle="Optional — can also be added after creation">
      {content}
    </FormSection>
  );
};

// ─────────────────────────────────────────────────────────────
// Junction field dispatcher
// ─────────────────────────────────────────────────────────────

interface JunctionFieldProps {
  schema: JunctionSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  policyId: string;
}

const JunctionField = ({ schema, value, onChange, policyId }: JunctionFieldProps) => {
  switch (schema.junctionType) {
    case "string_array":
      return (
        <EditableCodeChipSection
          label={schema.label}
          codes={(value as string[]) ?? []}
          color={schema.chipColor ?? "icd"}
          canEdit
          onUpdate={onChange as (codes: string[]) => void}
          placeholder={schema.placeholder}
          maxLength={schema.maxLength}
          defaultEditing
        />
      );

    case "codes":
      return <CodesField label={schema.label} value={value as EndorsementCodes} onChange={onChange} />;

    case "qualifying_conditions":
      return (
        <QualifyingConditionsField
          label={schema.label}
          selected={(value as QualifyingCondition[]) ?? []}
          onChange={onChange as (conditions: QualifyingCondition[]) => void}
        />
      );

    case "claim_ids":
      return (
        <ClaimIdPicker
          policyId={policyId}
          claimIds={(value as string[]) ?? []}
          canEdit
          onUpdate={onChange as (ids: string[]) => void}
        />
      );

    case "tiers":
      return (
        <TiersField
          label={schema.label}
          tiers={(value as TieredDeductibleTier[]) ?? []}
          onChange={onChange as (tiers: TieredDeductibleTier[]) => void}
        />
      );

    case "admin_change_entries":
      return (
        <AdminChangeEntriesField
          label={schema.label}
          entries={(value as AdminChangeEntry[]) ?? []}
          onChange={onChange as (entries: AdminChangeEntry[]) => void}
        />
      );

    case "amendment_entries":
      return (
        <AmendmentEntriesField
          label={schema.label}
          entries={(value as AmendmentEntry[]) ?? []}
          onChange={onChange as (entries: AmendmentEntry[]) => void}
        />
      );

    case "domestic_reimbursement_entries":
      return (
        <DomesticReimbursementEntriesField
          label={schema.label}
          entries={(value as DomesticReimbursementEntry[]) ?? []}
          onChange={onChange as (entries: DomesticReimbursementEntry[]) => void}
        />
      );

    default:
      return null;
  }
};

// ─────────────────────────────────────────────────────────────
// Codes field (ICD + CPT)
// ─────────────────────────────────────────────────────────────

const CodesField = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: EndorsementCodes;
  onChange: (codes: unknown) => void;
}) => (
  <Box>
    <Typography
      variant="overline"
      sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.disabled", display: "block", mt: 2, mb: 0 }}
    >
      {label}
    </Typography>
    <EditableCodeChipSection
      label="ICD Codes"
      codes={value?.icd ?? []}
      color="icd"
      canEdit
      onUpdate={(icd) => onChange({ ...value, icd })}
      placeholder="e.g. E11.9"
      maxLength={8}
      defaultEditing
    />
    <EditableCodeChipSection
      label="CPT Codes"
      codes={value?.cpt ?? []}
      color="cpt"
      canEdit
      onUpdate={(cpt) => onChange({ ...value, cpt })}
      placeholder="e.g. 99213"
      maxLength={5}
      defaultEditing
    />
  </Box>
);

// ─────────────────────────────────────────────────────────────
// Qualifying conditions chip picker
// ─────────────────────────────────────────────────────────────

const QualifyingConditionsField = ({
  label,
  selected,
  onChange,
}: {
  label: string;
  selected: QualifyingCondition[];
  onChange: (conditions: QualifyingCondition[]) => void;
}) => {
  const { theme, isDarkMode } = useAppTheme();

  const handleToggle = (condition: QualifyingCondition) => {
    const isSelected = selected.includes(condition);
    onChange(isSelected ? selected.filter((c) => c !== condition) : [...selected, condition]);
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 0.5 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.disabled" }}>
          {label}
        </Typography>
      </Box>
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          p: 1.5,
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {ALL_QUALIFYING_CONDITIONS.map((condition) => {
            const isSelected = selected.includes(condition);
            return (
              <Chip
                key={condition}
                label={formatToTitleCase(condition)}
                size="small"
                onClick={() => handleToggle(condition)}
                sx={{
                  cursor: "pointer",
                  bgcolor: isSelected ? "#fef3c7" : isDarkMode ? theme.palette.grey[800] : theme.palette.grey[100],
                  color: isSelected ? "#92400e" : "text.secondary",
                  fontWeight: isSelected ? 600 : 400,
                  border: isSelected ? "1px solid #fbbf24" : "1px solid transparent",
                  "&:hover": {
                    bgcolor: isSelected ? "#fde68a" : isDarkMode ? theme.palette.grey[700] : theme.palette.grey[200],
                  },
                }}
              />
            );
          })}
        </Box>
        {selected.length === 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ mt: 0.75, display: "block", fontStyle: "italic" }}>
            Click to select qualifying conditions
          </Typography>
        )}
      </Box>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Tiers table
// ─────────────────────────────────────────────────────────────

const TIER_GRID = "0.4fr 1fr 0.8fr 0.8fr 0.7fr 0.3fr";

const TiersField = ({
  label,
  tiers,
  onChange,
}: {
  label: string;
  tiers: TieredDeductibleTier[];
  onChange: (tiers: TieredDeductibleTier[]) => void;
}) => {
  const { theme } = useAppTheme();
  const [deductible, setDeductible] = useState("");
  const [ageStart, setAgeStart] = useState("");
  const [ageEnd, setAgeEnd] = useState("");
  const [reimbPct, setReimbPct] = useState("");

  const nextTierOrder = tiers.length === 0 ? 1 : Math.max(...tiers.map((t) => t.tier_order)) + 1;

  const canAdd =
    deductible !== "" &&
    ageStart !== "" &&
    ageEnd !== "" &&
    reimbPct !== "" &&
    !isNaN(parseFloat(deductible)) &&
    !isNaN(parseInt(ageStart, 10)) &&
    !isNaN(parseInt(ageEnd, 10)) &&
    !isNaN(parseFloat(reimbPct));

  const handleAdd = () => {
    if (!canAdd) return;
    const newTier: TieredDeductibleTier = {
      id: crypto.randomUUID(),
      tier_order: nextTierOrder,
      deductible: parseFloat(deductible),
      age_range_start: parseInt(ageStart, 10),
      age_range_end: parseInt(ageEnd, 10),
      reimbursement_pct: parseFloat(reimbPct) / 100,
    };
    onChange([...tiers, newTier]);
    setDeductible("");
    setAgeStart("");
    setAgeEnd("");
    setReimbPct("");
  };

  const handleDelete = (id: string) => {
    const remaining = tiers.filter((t) => t.id !== id);
    // Re-order remaining tiers sequentially
    onChange(remaining.map((t, i) => ({ ...t, tier_order: i + 1 })));
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 0.5 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.disabled" }}>
          {label}
        </Typography>
      </Box>
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          p: 1.5,
        }}
      >
        {/* Header */}
        <Box sx={{ display: "grid", gridTemplateColumns: TIER_GRID, gap: 0.5, mb: 0.5 }}>
          {["Tier", "Deductible", "Age Start", "Age End", "Reimb. %", ""].map((h) => (
            <Typography key={h} variant="caption" fontWeight={700} color="text.secondary">
              {h}
            </Typography>
          ))}
        </Box>

        {/* Existing rows */}
        {tiers.map((tier) => (
          <Box
            key={tier.id}
            sx={{ display: "grid", gridTemplateColumns: TIER_GRID, gap: 0.5, alignItems: "center", py: 0.25 }}
          >
            <Typography variant="body2">{tier.tier_order}</Typography>
            <Typography variant="body2">${tier.deductible.toLocaleString()}</Typography>
            <Typography variant="body2">{tier.age_range_start}</Typography>
            <Typography variant="body2">{tier.age_range_end}</Typography>
            <Typography variant="body2">{parseFloat((tier.reimbursement_pct * 100).toPrecision(10))}%</Typography>
            <IconButton size="small" onClick={() => handleDelete(tier.id)} sx={{ p: 0 }}>
              <Close sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}

        {/* Add row */}
        <Box sx={{ display: "grid", gridTemplateColumns: TIER_GRID, gap: 0.5, alignItems: "center", mt: 0.5 }}>
          <Typography variant="body2" color="text.disabled">
            {nextTierOrder}
          </Typography>
          <TextField
            size="small"
            type="number"
            placeholder="$"
            value={deductible}
            onChange={(e) => setDeductible(e.target.value)}
            sx={compactInputSx}
          />
          <TextField
            size="small"
            type="number"
            placeholder="Start"
            value={ageStart}
            onChange={(e) => setAgeStart(e.target.value)}
            sx={compactInputSx}
          />
          <TextField
            size="small"
            type="number"
            placeholder="End"
            value={ageEnd}
            onChange={(e) => setAgeEnd(e.target.value)}
            sx={compactInputSx}
          />
          <TextField
            size="small"
            type="number"
            placeholder="%"
            value={reimbPct}
            onChange={(e) => setReimbPct(e.target.value)}
            sx={compactInputSx}
          />
          <IconButton size="small" onClick={handleAdd} disabled={!canAdd} sx={{ p: 0 }}>
            <Add sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {tiers.length === 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ mt: 0.75, display: "block", fontStyle: "italic" }}>
            No tiers added yet
          </Typography>
        )}
      </Box>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Admin change entries table
// ─────────────────────────────────────────────────────────────

const AdminChangeEntriesField = ({
  label,
  entries,
  onChange,
}: {
  label: string;
  entries: AdminChangeEntry[];
  onChange: (entries: AdminChangeEntry[]) => void;
}) => {
  const { theme } = useAppTheme();
  const [changeType, setChangeType] = useState("");
  const [date, setDate] = useState("");

  const canAdd = changeType.trim() !== "" && date !== "";

  const handleAdd = () => {
    if (!canAdd) return;
    const newEntry: AdminChangeEntry = {
      id: crypto.randomUUID(),
      admin_change_type: changeType.trim(),
      effective_date: date,
    };
    onChange([...entries, newEntry]);
    setChangeType("");
    setDate("");
  };

  const handleDelete = (id: string) => {
    onChange(entries.filter((e) => e.id !== id));
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 0.5 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.disabled" }}>
          {label}
        </Typography>
      </Box>
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          p: 1.5,
        }}
      >
        {/* Header */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 0.75fr 32px", gap: 1, mb: 0.5 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            Change Type
          </Typography>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            Effective Date
          </Typography>
          <Box />
        </Box>

        {/* Existing rows */}
        {entries.map((entry) => (
          <Box
            key={entry.id}
            sx={{ display: "grid", gridTemplateColumns: "1fr 0.75fr 32px", gap: 1, alignItems: "center", py: 0.25 }}
          >
            <Typography variant="body2">{entry.admin_change_type}</Typography>
            <Typography variant="body2">{entry.effective_date}</Typography>
            <IconButton size="small" onClick={() => handleDelete(entry.id)} sx={{ p: 0 }}>
              <Close sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}

        {/* Add row */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 0.75fr 32px", gap: 1, alignItems: "center", mt: 0.5 }}>
          <TextField
            size="small"
            placeholder="Change type..."
            value={changeType}
            onChange={(e) => setChangeType(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && canAdd && handleAdd()}
            sx={compactInputSx}
          />
          <TextField
            size="small"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={compactInputSx}
          />
          <IconButton size="small" onClick={handleAdd} disabled={!canAdd} sx={{ p: 0 }}>
            <Add sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {entries.length === 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ mt: 0.75, display: "block", fontStyle: "italic" }}>
            No entries added yet
          </Typography>
        )}
      </Box>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Amendment entries table
// ─────────────────────────────────────────────────────────────

const AmendmentEntriesField = ({
  label,
  entries,
  onChange,
}: {
  label: string;
  entries: AmendmentEntry[];
  onChange: (entries: AmendmentEntry[]) => void;
}) => {
  const { theme } = useAppTheme();
  const nextNumber = entries.length === 0 ? 1 : Math.max(...entries.map((e) => e.amendment_number)) + 1;
  const [amendmentNumber, setAmendmentNumber] = useState(String(nextNumber));
  const [date, setDate] = useState("");

  // Update default number when entries change
  useEffect(() => {
    const next = entries.length === 0 ? 1 : Math.max(...entries.map((e) => e.amendment_number)) + 1;
    setAmendmentNumber(String(next));
  }, [entries]);

  const parsedNumber = parseInt(amendmentNumber, 10);
  const isDuplicate = !isNaN(parsedNumber) && entries.some((e) => e.amendment_number === parsedNumber);
  const canAdd = amendmentNumber !== "" && !isNaN(parsedNumber) && !isDuplicate && date !== "";

  const handleAdd = () => {
    if (!canAdd) return;
    const newEntry: AmendmentEntry = {
      id: crypto.randomUUID(),
      amendment_number: parsedNumber,
      effective_date: date,
    };
    onChange([...entries, newEntry]);
    setDate("");
  };

  const handleDelete = (id: string) => {
    onChange(entries.filter((e) => e.id !== id));
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 0.5 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.disabled" }}>
          {label}
        </Typography>
      </Box>
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          p: 1.5,
        }}
      >
        {/* Header */}
        <Box sx={{ display: "grid", gridTemplateColumns: "80px 1fr 32px", gap: 1, mb: 0.5 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            #
          </Typography>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            Effective Date
          </Typography>
          <Box />
        </Box>

        {/* Existing rows */}
        {entries.map((entry) => (
          <Box
            key={entry.id}
            sx={{ display: "grid", gridTemplateColumns: "80px 1fr 32px", gap: 1, alignItems: "center", py: 0.25 }}
          >
            <Typography variant="body2">{entry.amendment_number}</Typography>
            <Typography variant="body2">{entry.effective_date}</Typography>
            <IconButton size="small" onClick={() => handleDelete(entry.id)} sx={{ p: 0 }}>
              <Close sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}

        {/* Add row */}
        <Box sx={{ display: "grid", gridTemplateColumns: "80px 1fr 32px", gap: 1, alignItems: "center", mt: 0.5 }}>
          <TextField
            size="small"
            type="number"
            placeholder="#"
            value={amendmentNumber}
            onChange={(e) => setAmendmentNumber(e.target.value)}
            error={isDuplicate}
            sx={compactInputSx}
          />
          <TextField
            size="small"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && canAdd && handleAdd()}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={compactInputSx}
          />
          <IconButton size="small" onClick={handleAdd} disabled={!canAdd} sx={{ p: 0 }}>
            <Add sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {isDuplicate && (
          <Typography variant="caption" color="error" sx={{ mt: 0.25, display: "block" }}>
            Amendment #{parsedNumber} already exists
          </Typography>
        )}

        {entries.length === 0 && !isDuplicate && (
          <Typography variant="caption" color="text.disabled" sx={{ mt: 0.75, display: "block", fontStyle: "italic" }}>
            No amendments added yet
          </Typography>
        )}
      </Box>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Domestic reimbursement entries table
// ─────────────────────────────────────────────────────────────

const DomesticReimbursementEntriesField = ({
  label,
  entries,
  onChange,
}: {
  label: string;
  entries: DomesticReimbursementEntry[];
  onChange: (entries: DomesticReimbursementEntry[]) => void;
}) => {
  const { theme } = useAppTheme();
  const [reason, setReason] = useState("");
  const [adjustmentPct, setAdjustmentPct] = useState("");
  const [newPosCodes, setNewPosCodes] = useState<string[]>([]);

  const canAdd = reason.trim() !== "" && adjustmentPct !== "" && !isNaN(parseFloat(adjustmentPct));

  const handleAdd = () => {
    if (!canAdd) return;
    const newEntry: DomesticReimbursementEntry = {
      id: crypto.randomUUID(),
      reason: reason.trim(),
      adjustment_percentage: parseFloat(adjustmentPct) / 100, // UI % → decimal
      ...(newPosCodes.length > 0 ? { pos_codes: newPosCodes } : {}),
    };
    onChange([...entries, newEntry]);
    setReason("");
    setAdjustmentPct("");
    setNewPosCodes([]);
  };

  const handleDelete = (id: string) => {
    onChange(entries.filter((e) => e.id !== id));
  };

  /** Update POS codes for a specific entry */
  const handleUpdatePosCodes = (entryId: string, posCodes: string[]) => {
    onChange(entries.map((e) => (e.id === entryId ? { ...e, pos_codes: posCodes } : e)));
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 0.5 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.disabled" }}>
          {label}
        </Typography>
      </Box>
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          p: 1.5,
        }}
      >
        {/* Header */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 0.5fr 1fr 32px", gap: 1, mb: 0.5 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            Reason
          </Typography>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            Adj. %
          </Typography>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            POS Codes
          </Typography>
          <Box />
        </Box>

        {/* Existing rows — each entry includes its POS codes inline */}
        {entries.map((entry) => (
          <Box
            key={entry.id}
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 0.5fr 1fr 32px",
              gap: 1,
              alignItems: "center",
              py: 0.25,
              borderBottom: `1px solid ${theme.palette.divider}`,
              "&:last-child": { borderBottom: "none" },
            }}
          >
            <Typography variant="body2">{entry.reason}</Typography>
            <Typography variant="body2">{parseFloat((entry.adjustment_percentage * 100).toPrecision(10))}%</Typography>
            <InlinePosCodesCreate
              codes={entry.pos_codes ?? []}
              onUpdate={(codes) => handleUpdatePosCodes(entry.id, codes)}
            />
            <IconButton size="small" onClick={() => handleDelete(entry.id)} sx={{ p: 0 }}>
              <Close sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}

        {/* Add row */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 0.5fr 1fr 32px", gap: 1, alignItems: "center", mt: 0.5 }}>
          <TextField
            size="small"
            placeholder="Reason..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && canAdd && handleAdd()}
            sx={compactInputSx}
          />
          <TextField
            size="small"
            type="number"
            placeholder="%"
            value={adjustmentPct}
            onChange={(e) => setAdjustmentPct(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && canAdd && handleAdd()}
            sx={compactInputSx}
          />
          <InlinePosCodesCreate codes={newPosCodes} onUpdate={setNewPosCodes} />
          <IconButton size="small" onClick={handleAdd} disabled={!canAdd} sx={{ p: 0 }}>
            <Add sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {entries.length === 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ mt: 0.75, display: "block", fontStyle: "italic" }}>
            No entries added yet
          </Typography>
        )}
      </Box>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Inline POS code chips for DRA entry rows (create form)
// ─────────────────────────────────────────────────────────────

const POS_CHIP_COLORS = { bg: "#fff7ed", color: "#9a3412" };

const InlinePosCodesCreate = ({ codes, onUpdate }: { codes: string[]; onUpdate: (codes: string[]) => void }) => {
  const [input, setInput] = useState("");

  const handleAdd = () => {
    const trimmed = input.trim().toUpperCase();
    if (!trimmed || codes.includes(trimmed)) {
      setInput("");
      return;
    }
    onUpdate([...codes, trimmed]);
    setInput("");
  };

  const handleRemove = (code: string) => {
    onUpdate(codes.filter((c) => c !== code));
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.25, alignItems: "center", minHeight: 22 }}>
      {codes.map((code) => (
        <Chip
          key={code}
          label={code}
          size="small"
          onDelete={() => handleRemove(code)}
          sx={{
            height: 20,
            bgcolor: POS_CHIP_COLORS.bg,
            color: POS_CHIP_COLORS.color,
            fontFamily: "monospace",
            fontSize: "0.7rem",
            fontWeight: 500,
            "& .MuiChip-deleteIcon": { color: POS_CHIP_COLORS.color, fontSize: 12 },
            "& .MuiChip-label": { px: 0.5 },
          }}
        />
      ))}
      <TextField
        size="small"
        placeholder="+"
        value={input}
        onChange={(e) => setInput(e.target.value.slice(0, 4))}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleAdd();
          }
        }}
        sx={{
          width: 44,
          "& .MuiInputBase-root": { height: 22, fontSize: "0.7rem", fontFamily: "monospace" },
          "& .MuiInputBase-input": { py: 0, px: 0.5 },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: POS_CHIP_COLORS.color },
        }}
      />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────
// Shared styles
// ─────────────────────────────────────────────────────────────

/** Compact inline input style for table rows */
const compactInputSx = {
  "& .MuiInputBase-root": { height: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.5, px: 0.75 },
};
